/**
 * @file Game over state.
 *
 * The world keeps its last frame on screen while particles finish playing, so
 * the state still ticks the world — it simply no longer has a player entity to
 * drive. Restarting is a plain transition back through loading.
 */

import { GameState } from './GameState.js';
import { GameStates } from './StateMachine.js';
import { Action } from '../../input/InputManager.js';
import { prepareNewRun } from '../../world/Level.js';

/** Seconds of input lockout, so the shot that killed you cannot skip the screen. */
const INPUT_LOCKOUT = 0.6;

export class GameOverState extends GameState {
  /**
   * @param {import('../Context.js').GameContext} ctx Shared game context.
   * @param {import('./StateMachine.js').StateMachine} machine Owning machine.
   */
  constructor(ctx, machine) {
    super(ctx, machine);
    /** @type {number} @private */
    this._lockout = 0;
  }

  /** @returns {void} */
  enter() {
    this._lockout = INPUT_LOCKOUT;
    this.ctx.player = null;
    this.ctx.input.releaseAll();
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    // Keep ticking so the death burst and the remaining enemies settle.
    this.ctx.world.update(dt);

    if (this._lockout > 0) {
      this._lockout -= dt;
      return;
    }

    const { input } = this.ctx;
    // KeyR is bound to RELOAD (ammo in play); treat it as restart here too.
    if (
      input.justPressed(Action.RESTART) ||
      input.justPressed(Action.RELOAD) ||
      input.justPressed(Action.FIRE) ||
      input.justPressed(Action.CONFIRM)
    ) {
      prepareNewRun(this.ctx);
      this.machine.change(GameStates.LOADING);
    } else if (input.justPressed(Action.PAUSE)) {
      this.machine.change(GameStates.MENU);
    }
  }
}
